import React, { useState, useRef } from 'react';
import { styled } from '@mui/material/styles';
import moment from 'moment';
import { isEqual } from 'lodash';
import {
  ErrorQuestionDto,
  QuestionResponseDto,
  QuestionGroupDto,
  QuestionDto,
  SectionDto,
  SectionResponseDto,
  SectionResponseUpdatedDto,
  SubSectionDto,
  SubSectionResponseDto,
} from 'models/dtoTypes';
import { AxiosResponse } from 'axios';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import LinearProgress from '@mui/material/LinearProgress';
import ToastAlert from 'components/ToastAlert';

import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Typography from '@mui/material/Typography';

import Formsy from 'formsy-react';
import Question from 'components/Question';
import matchConditions from 'utils/matchConditions';
import { getSubSectionFieldWidget, generateSingleResponse } from 'utils/sectionUtils';
import { useTranslation } from 'react-i18next';

const PREFIX = 'UpdateSubSectionDialog';

const classes = {
  root: `${PREFIX}-root`,
  dialogContent: `${PREFIX}-dialogContent`,
  button: `${PREFIX}-button`,
  submitButton: `${PREFIX}-submitButton`,
  flashSpacer: `${PREFIX}-flashSpacer`,
  conditionalQuestion: `${PREFIX}-conditionalQuestion`,
  paper: `${PREFIX}-paper`,
};

const StyledDialog = styled(Dialog)(() => ({
  [`&.${classes.root}`]: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
  },

  [`& .${classes.paper}`]: {
    overflowY: 'visible', // do not cover dropdown
  },

  [`& .${classes.dialogContent}`]: {
    minWidth: 600,
    // minHeight: 500,
    flex: 1,
    overflowY: 'visible',
  },

  [`& .${classes.submitButton}`]: {
    minWidth: 100,
    height: 50,
  },

  [`& .${classes.flashSpacer}`]: {
    minHeight: 10,
  },

  [`& .${classes.conditionalQuestion}`]: {
    marginLeft: '30px',
    marginTop: '-20px',
  },
}));

type QuestionGroup = QuestionGroupDto & {
  questions: QuestionDto[];
};

// base64 is handled by 'material-ui-dropzone' package when files are selected
type FileValue = {
  data: string;
  file: File;
};

type HttpRequest = AxiosResponse<SectionResponseDto | ErrorQuestionDto>;

type ValueType = string | number | boolean | FileValue | FileValue[] | string[] | moment.Moment;

interface Attributes {
  [key: string]: ValueType;
}

interface OnClose {
  (): void;
}

interface OnSave {
  (
    questionResponses: QuestionResponseDto[],
  ): Promise<AxiosResponse<SectionResponseDto | ErrorQuestionDto> | void>;
}

interface OnDeleteFile {
  (fileId: string): void;
}

interface OnUpdateSection {
  (sectionResponse: SectionResponseUpdatedDto): void;
}

interface Props {
  questionLabel?: string;
  sectionAlias?: string;
  subSectionResponse?: SubSectionResponseDto | null;
  subSectionId?: string;
  subSectionAlias?: string;
  onUpdateSection?: OnUpdateSection;
  label: string;
  subSection?: SubSectionDto;
  open: boolean;
  onDeleteFile: OnDeleteFile;
  onClose: OnClose;
  onSave: OnSave;
  companyId?: string;
}

function UpdateSubSectionDialog(props: Props): JSX.Element {
  const formRef = useRef<Formsy>(null);
  const {
    companyId,
    sectionAlias,
    subSection,
    subSectionResponse,
    label, // dialog title
    questionLabel, // question label
    open,
    onClose,
    onSave,
    onDeleteFile,
  } = props;
  const [httpResponse, setHttpResponse] = useState<HttpRequest | undefined>(undefined);
  const [loading, setLoading] = useState(false);
  const [questionGroups, setQuestionGroups] = useState<QuestionGroup[]>(
    generateQuestionGroups(subSection),
  );
  const { t } = useTranslation('default');

  function isChanged() {
    const formsyEl = formRef.current;
    return formsyEl ? formsyEl.isChanged() : false;
  }

  function getInitialValues(sectionData: SectionDto) {
    if (!sectionData) {
      return {};
    }
    const currentValues = {};
    return currentValues;
  }

  function getCurrentValues() {
    const formsyEl = formRef.current;
    return (formsyEl ? formsyEl.getCurrentValues() : {}) as Attributes;
  }

  function generateQuestionGroups(sectionData?: SubSectionDto): QuestionGroup[] {
    if (!sectionData) {
      return [];
    }
    const currentValues = isChanged() ? getCurrentValues() : getInitialValues(sectionData);
    const { questions = [] } = sectionData;

    const filteredQuestion = questions.filter((question) =>
      matchConditions(question.conditions, currentValues),
    );

    const groups: QuestionGroup[] = [];

    let currentGroup: QuestionGroup;

    filteredQuestion.forEach((question) => {
      if (!currentGroup || currentGroup.alias !== question.questionGroupAlias) {
        const alias = question.questionGroupAlias || `group-${groups.length}`;
        const group = sectionData.questionGroups?.find(
          (g) => g.alias === question.questionGroupAlias,
        );
        const { label } = group || {};
        currentGroup = {
          alias,
          label,
          questions: [],
        };
        groups.push(currentGroup);
      }
      currentGroup.questions.push(question);
    });

    return groups;
  }

  const handleGenerateSubSectionFileData = (
    subQuestionAlias: string,
    data: string,
    fileName: string,
    extension: string,
  ) => {
    const { subSectionId = '', subSectionAlias = '' } = props;
    const subSectionResponses = [
      {
        subSectionId: subSectionId,
        questionAlias: subSectionAlias,
        questionResponses: [
          {
            questionAlias: subQuestionAlias,
            type: 'QuestionResponseFileDto',
            data,
            fileName,
            extension,
          },
        ],
      },
    ];

    const body = {
      companyId: companyId,
      sectionAlias: sectionAlias || '',
      questionResponses: [],
      subSectionResponses,
    };
    return body;
  };

  const generateQuestionResponses = (model: Attributes): QuestionResponseDto[] => {
    if (!subSection) return [];
    const questionMapper = {};
    const { questions } = subSection;
    if (Array.isArray(questions)) {
      questions.forEach((field) => {
        if (field.alias) {
          questionMapper[field.alias] = field.type;
        }
      });
    }
    const questionResponses = [];
    const fields = Object.entries(model).filter((f) => f[1] !== undefined);

    for (const field of fields) {
      const [questionAlias, value] = field;
      const type = questionMapper[questionAlias] as string;
      if (type === 'QuestionFileListDto') {
        const files = (Array.isArray(value) ? value : []) as FileValue[];
        for (const file of files) {
          const response = generateSingleResponse(questionAlias, 'QuestionFileListDto', file);
          questionResponses.push(response);
        }
      } else {
        const response = generateSingleResponse(questionAlias, type, value);
        questionResponses.push(response);
      }
    }
    return questionResponses;
  };

  const handleClose = () => {
    setHttpResponse(undefined);
    onClose();
  };

  const handleChange = () => {
    if (!subSection) {
      return;
    }
    const newQuestionGroups = generateQuestionGroups(subSection);
    if (!isEqual(newQuestionGroups, questionGroups)) {
      setQuestionGroups(newQuestionGroups);
    }
  };

  const disableButton = () => {
    console.log('disable button');
  };

  const enableButton = () => {
    console.log('enable button');
  };

  async function handleCreate(e: React.FormEvent) {
    e.stopPropagation();
    const model = getCurrentValues();
    console.log('nested model', model);
    const questionResponses = generateQuestionResponses(model);
    setLoading(true);
    const response = await (onSave && onSave(questionResponses));
    setLoading(false);
    if (response) {
      setHttpResponse(response);
    }
  }

  const getResponses = (
    field: QuestionDto,
    response?: SubSectionResponseDto | null,
  ): QuestionResponseDto[] | undefined => {
    if (!response) {
      return [];
    }
    return response?.responses?.filter((r) => r.questionAlias === field.alias);
  };

  // nested form is bad, change to element 'div'
  const renderCardContent = () => (
    <Formsy
      formElement="div"
      onValid={enableButton}
      onChange={handleChange}
      onInvalid={disableButton}
      ref={formRef}
      className="flex flex-col justify-center"
    >
      <Typography component="h3" variant="h6" color="inherit">
        {questionLabel}
      </Typography>
      {questionGroups.map((group) => (
        <div key={group.alias}>
          <h3>{group.label}</h3>
          {group.questions.map((field) => (
            <div
              key={field.alias}
              className={field.conditions?.length !== 0 ? classes.conditionalQuestion : ''}
            >
              <Question
                disabled={loading}
                questionDto={field}
                widget={getSubSectionFieldWidget(field)}
                onGeneratePostFileData={handleGenerateSubSectionFileData}
                onDeleteFile={field.type === 'QuestionFileListDto' ? onDeleteFile : undefined}
                onUpdateSection={props.onUpdateSection}
                responses={getResponses(field, subSectionResponse)}
              />
            </div>
          ))}
        </div>
      ))}
    </Formsy>
  );

  const renderToastAlert = () => {
    if (!httpResponse) return <></>;
    const open = Boolean(httpResponse);
    const message =
      httpResponse.status === 200 ? 'Your answers have been saved' : 'The responses are not valid.';
    const severity = httpResponse.status === 200 ? 'success' : 'error';
    return (
      <ToastAlert
        open={open}
        severity={severity}
        message={message}
        onClose={() => setHttpResponse(undefined)}
      />
    );
  };

  return (
    <StyledDialog
      maxWidth="md"
      open={open}
      onClose={handleClose}
      className={classes.root}
      PaperProps={{
        className: classes.paper,
      }}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
    >
      <Box boxShadow={2}>
        <DialogTitle>{label}</DialogTitle>
      </Box>
      <DialogContent className={classes.dialogContent}>
        <div>{renderToastAlert()}</div>
        {renderCardContent()}
        <div style={{ margin: 16 }} />
        <DialogActions>
          <Button disabled={loading} onClick={handleClose} color="primary">
            {t('cancel')}
          </Button>
          <Button disabled={loading} color="primary" autoFocus onClick={handleCreate}>
            {t('submit')}
          </Button>
        </DialogActions>
      </DialogContent>
      {loading && <LinearProgress />}
    </StyledDialog>
  );
}

export default UpdateSubSectionDialog;
